/* eslint-disable max-lines */
import {
	FETCH_SUBSCRIPTION, UPDATE_SUBSCRIPTION, UPDATE_SHIPPING_ADDRESS, CLEAR_SUBSCRIPTION_CART,
	TOGGLE_SUBS_CART, LIST_SCHEDULED_ORDERS, TOGGLE_UPDATING_SUB_PRODUCT, TOGGLE_RESCHEDULE_MODAL,
	LIST_PAYMENT_METHODS, CHANGE_PAYMENT_METHOD, LOGOUT, TOGGLE_SCHEDULED_ORDERS_MODAL, UPDATE_REACTIVATION_FLOW,
	EDIT_LINE_ITEM, REMOVE_LINE_ITEM, CREATE_LINE_ITEM, UPDATE_LINE_TO_CREATE, TOGGLE_UNAVAILABLE_ITEMS,
	TOGGLE_SAVE_LOADING, CHANGE_NEXT_BILLING_DATE, UPDATE_DELIVERY_FREQUENCY, LIST_ORDER_HISTORY,
	UPDATE_DELIVERY_PRICE, UPDATE_SUBSCRIPTION_BOX, DISCARD_CHANGES, TOGGLE_SAVE_POPUP, SET_HIGHLIGHT_BANNER,
	SET_CLIENT_SUB_STATUS, SET_AVAILABLE_SHIPPING_RATES
} from "utils/action-types";
import { getCustomerProfile } from "actions/account/profile";
import { cloneLineItems } from "helpers/map-eversubs-subscription-lines";
import { getCurrentDiscountStep } from "utils/discounts";
import isBundleWithSubProducts from "utils/bundle";

const initialState = {
	subscriptionId: null,
	discounts: [],
	totalLineItemPrice: 0,
	totalWeight: 0,
	totalLinesCount: 0,
	totalLineItemDiscountedPrice: 0,
	deliveryPrice: 0,
	deliveryMethod: {
		code: "",
		title: ""
	},
	status: "",
	nextBillingDate: null,
	nextBillingDateEpoch: null,
	updatedAt: null,
	paymentMethodId: null,
	deliveryPolicy: {},
	shippingAddress: null,
	deliveryLocationId: null,
	lines: {},

	originalLines: {},
	paymentMethods: [],
	isActive: true,
	isCancelled: false,
	isExpired: false,
	isCartOpen: false,
	scheduledOrders: {},
	orderHistory: [],
	isScheduledOrdersModalOpen: false,
	reschedule: {
		isModalOpen: false,
		skipSurvey: false,
	},
	reactivationFlow: {
		step: null,
		isCompleted: false,
		isActive: false,
		mealsConfirmed: false
	},
	draftChanges: {
		linesToCreate: {},
		linesToUpdate: {},
		linesToRemove: {},
		unsaved: false, // Indicates if there are draft changes that haven't been saved
		isSyncing: false, // Indicates if the draft changes are being synced with the server (Loop API)
		savePrompt: {
			isVisible: false,
			redirectPath: null
		}
	},
	isFetched: false,
	unavailable_items: [],
	activeBox: {
		userSelected: false,
	},
	chargeOffsetDays: 0,
	deliveryDate: null,
	commitmentBasedDiscount: null,
	removeCount: 0,
	highlightBanner: false,
	clientSubStatus: null,
	sequentialDiscount: null,
	availableShippingRates: [],
	shippingRatesLoading: false
};

const getLineAttributes = (item = {}) => {
	const attributes = item?.customAttributes || item?.attributes || [];
	return Array.isArray(attributes) ? attributes : [];
};

const getLineAttributeValue = (item = {}, key) =>
	getLineAttributes(item).find((attr) => attr?.key === key)?.value ?? null;

const normalizeBundleKey = (value) =>
	(typeof value === "string" && value.trim() ? value.trim().toLowerCase() : null);

const getBundleKey = (item = {}) =>
	normalizeBundleKey(
		getLineAttributeValue(item, "_sdid") ||
		getLineAttributeValue(item, "_bid") ||
		getLineAttributeValue(item, "In bundle")
	);

const isFreeGiftLikeItem = (item = {}) => {
	const type = String(getLineAttributeValue(item, "type") || item?.type || "").toLowerCase();

	return Boolean(item?.isFreeGift) ||
		Number(item?.basePrice) === 0 ||
		(Number(item?.price) === 0 && Number(item?.discountedPrice) === 0) ||
		getLineAttributes(item).some((attr) => String(attr?.key || "").toLowerCase() === "_freegift") ||
		type === "one_time" ||
		Boolean(item?.isOneTimeAdded);
};

const isBundleFreeGiftItem = (item = {}) =>
	Boolean(getBundleKey(item)) && isFreeGiftLikeItem(item);

const shouldCascadeBundleFreeGiftRemoval = (item = {}) =>
	Boolean(getBundleKey(item)) &&
	!isBundleWithSubProducts(item) &&
	!isBundleFreeGiftItem(item);

const collectBundleFreeGiftItems = (lineItems = {}, removedItem = {}) => {
	if (!shouldCascadeBundleFreeGiftRemoval(removedItem))
		return [];

	const removedBundleKey = getBundleKey(removedItem);
	if (!removedBundleKey) return [];

	const candidates = Object.values(lineItems)
		.flatMap((line) => [line, ...(Array.isArray(line?.products_included) ? line.products_included : [])])
		.filter(Boolean);

	return candidates.filter((candidate) =>
		candidate?.id &&
		candidate.id !== removedItem.id &&
		isBundleFreeGiftItem(candidate) &&
		getBundleKey(candidate) === removedBundleKey
	);
};

const filterRemovedItemsFromBundleContainers = (collection = {}, removeIds = new Set()) =>
	Object.entries(collection).reduce((acc, [key, item]) => {
		if (!item || removeIds.has(item.id))
			return acc;

		if (!Array.isArray(item.products_included) || !item.products_included.length) {
			acc[key] = item;
			return acc;
		}

		const remainingSubproducts = item.products_included.filter((subItem) => !removeIds.has(subItem?.id));
		if (!remainingSubproducts.length)
			return acc;

		acc[key] = remainingSubproducts.length === item.products_included.length
			? item
			: { ...item, products_included: remainingSubproducts };
		return acc;
	}, {});

const normalizeWeightInKg = (item = {}) => {
	const weightInGrams = Number(item?.weightInGrams);
	if (!Number.isFinite(weightInGrams) || weightInGrams <= 0) return 0;

	return weightInGrams >= 1000 ? weightInGrams / 1000 : weightInGrams <= 1 ? 0 : weightInGrams;
};

const getLineQuantity = (item = {}) => {
	const quantity = Number(item?.totalQuantity ?? item?.quantity ?? item?.__quantity ?? 0);
	return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
};

const calculateTotalWeight = (lineItems) => Object.values(lineItems).reduce((acc, item) => {
	if (Array.isArray(item?.products_included) && item.products_included.length) {
		return acc + item.products_included.reduce((subAcc, subItem) =>
			subAcc + getLineQuantity(subItem) * normalizeWeightInKg(subItem), 0);
	}

	return acc + getLineQuantity(item) * normalizeWeightInKg(item);
}, 0);

const calculateTotalLinesCount = (lineItems) => Object.values(lineItems).reduce((acc, item) => {
	// If item is a bundle with subproducts, sum up subproduct quantities
	if (Array.isArray(item.products_included) && item.products_included.length > 0) {
		return acc + item.products_included.reduce(
			(subAcc, subItem) => subAcc + getLineQuantity(subItem), 0
		);
	}
	return acc + getLineQuantity(item);
}, 0);

const hasPendingChanges = (linesToCreate, linesToUpdate, linesToRemove) => (
	Object.keys(linesToCreate).length > 0 ||
	Object.keys(linesToUpdate).length > 0 ||
	Object.keys(linesToRemove).length > 0
);

const subscriptionReducer = (state = initialState, action) => {
	switch (action.type) {
		case FETCH_SUBSCRIPTION: {
			const { lines = {}, totalWeight, ...rest } = action.payload || {};
			const effectiveStatus = state.clientSubStatus === "PAUSED" && rest.status === "ACTIVE"
				? "PAUSED"
				: (rest.status || state.clientSubStatus || "ACTIVE");

			// If status is EXPIRED, treat as no subscription (clear subscriptionId)
			const isExpiredStatus = effectiveStatus === "EXPIRED";
			const effectiveSubscriptionId = isExpiredStatus ? null : rest.subscriptionId;

			return {
				...state,
				...rest,
				subscriptionId: effectiveSubscriptionId,
				status: effectiveStatus,
				isActive: effectiveStatus === "ACTIVE",
				isCancelled: effectiveStatus === "CANCELLED",
				isPaused: effectiveStatus === "PAUSED",
				isExpired: isExpiredStatus,
				lines: isExpiredStatus ? {} : lines,
				originalLines: isExpiredStatus ? {} : cloneLineItems(lines),
				totalWeight: isExpiredStatus ? 0 : (totalWeight ?? calculateTotalWeight(lines)),
				totalLinesCount: isExpiredStatus ? 0 : calculateTotalLinesCount(lines),
				draftChanges: initialState.draftChanges,
				isFetched: true,
				removeCount: 0,
			};
		}

		case UPDATE_REACTIVATION_FLOW: {
			return {
				...state,
				reactivationFlow: {
					...state.reactivationFlow,
					...action.payload,
				}
			};
		}

		case UPDATE_SUBSCRIPTION:
		{
			const effectiveStatus = action.payload?.status || state.clientSubStatus || "ACTIVE";

			return {
				...state,
				...action.payload,
				status: effectiveStatus,
				isActive: effectiveStatus === "ACTIVE",
				isCancelled: effectiveStatus === "CANCELLED",
				isPaused: effectiveStatus === "PAUSED",
				isExpired: effectiveStatus === "EXPIRED",
			};
		}

		case UPDATE_SUBSCRIPTION_BOX:
			return {
				...state,
				activeBox: {
					...state.box,
					...action.payload,
				}
			};

		case CHANGE_NEXT_BILLING_DATE:
			return {
				...state,
				nextBillingDate: action.payload.nextBillingDate,
				nextBillingDateEpoch: action.payload.nextBillingDateEpoch,
			};

		case UPDATE_DELIVERY_FREQUENCY:
			return {
				...state,
				deliveryPolicy: action.payload,
			};

		case LIST_SCHEDULED_ORDERS:
			return {
				...state,
				scheduledOrders: Object.fromEntries(action.payload.orders.map(order => [order.id, order])),
			};

		case LIST_ORDER_HISTORY:
			return {
				...state,
				orderHistory: action.payload.orders || [],
			};

		case UPDATE_DELIVERY_PRICE:
			return {
				...state,
				deliveryPrice: action.payload,
			};

		case UPDATE_SHIPPING_ADDRESS:
			return {
				...state,
				shippingAddress: action.payload
			};

		case LIST_PAYMENT_METHODS: {
			const methods = Array.isArray(action.payload) ? action.payload : [];
			let nextPaymentMethodId = state.paymentMethodId;

			if (!methods.length)
				nextPaymentMethodId = null;
			else if (!nextPaymentMethodId || !methods.some(method => method?.id === nextPaymentMethodId)) {
				const preferred = methods.find(method => method?.isDefault) ?? methods[0];
				nextPaymentMethodId = preferred?.id ?? null;
			}

			return {
				...state,
				paymentMethods: methods,
				paymentMethodId: nextPaymentMethodId
			};
		}

		case CHANGE_PAYMENT_METHOD:
			return {
				...state,
				paymentMethodId: action.payload
			};

		case TOGGLE_SUBS_CART:
			return {
				...state,
				isCartOpen: !state.isCartOpen
			};

		case TOGGLE_SCHEDULED_ORDERS_MODAL:
			return {
				...state,
				isScheduledOrdersModalOpen: !state.isScheduledOrdersModalOpen
			};

		case TOGGLE_RESCHEDULE_MODAL:
			return {
				...state,
				reschedule: {
					...state.reschedule,
					isModalOpen: !state.reschedule.isModalOpen,
					skipSurvey: action.skipSurvey || state.reschedule.skipSurvey,
				}
			};

		case TOGGLE_UPDATING_SUB_PRODUCT: {
			const currentLine = state.lines[action.productVariantId] || {};
			const nextUpdating = typeof action.isUpdating === "boolean" ?
				action.isUpdating :
				!currentLine?.updating;

			return {
				...state,
				lines: {
					...state.lines,
					[action.productVariantId]: {
						...currentLine,
						updating: nextUpdating,
						stock: action.stock !== undefined ? action.stock : currentLine.stock,
					}
				}
			};
		}

		case TOGGLE_UNAVAILABLE_ITEMS:
			return {
				...state,
				unavailable_items: action.payload
			};


		case TOGGLE_SAVE_LOADING:
			return {
				...state,
				draftChanges: {
					...state.draftChanges,
					isSyncing: !state.draftChanges.isSyncing
				},
			};

		case TOGGLE_SAVE_POPUP:
			return {
				...state,
				draftChanges: {
					...state.draftChanges,
					savePrompt: {
						isVisible: action.payload.isVisible,
						redirectPath: action.payload.redirectPath
					}
				}
			};

		case LOGOUT:
			return initialState;

		case EDIT_LINE_ITEM: {
			let {
				item,
				modifiedQuantity,
				modifiedTotalQuantity,
				resetRemoveCount,
			} = action.payload;

			const lineQuantity = {
				quantity: modifiedQuantity,
				totalQuantity: modifiedTotalQuantity,
			};

			let updatedLine = Object.assign({}, state.lines[item.variantShopifyId]);

			if (isBundleWithSubProducts(item)) {
				const subproducts = [];

				for (let i = 0; i < item.products_included.length; i++) {
					const subprod = item.products_included[i];

					subproducts.push({
						...subprod,
						quantity: modifiedQuantity * subprod.__quantity,
						totalQuantity: modifiedTotalQuantity * subprod.__quantity,
					});
				}

				updatedLine = Object.assign(updatedLine, { products_included: subproducts });

				item = {
					...item,
					products_included: subproducts
				};
			}

			const lines = {
				...state.lines,
				[item.variantShopifyId]: {
					...updatedLine,
					...lineQuantity,
				},
			};

			const linesToUpdate = {
				...state.draftChanges.linesToUpdate,
				[item.id]: { ...item, ...lineQuantity }
			};

			return {
				...state,
				draftChanges: {
					...state.draftChanges,
					linesToUpdate,
					unsaved: hasPendingChanges(state.draftChanges.linesToCreate, linesToUpdate, state.draftChanges.linesToRemove)
				},
				lines,
				totalWeight: calculateTotalWeight(lines),
				totalLinesCount: calculateTotalLinesCount(lines),
				removeCount: resetRemoveCount ? 0 : state.removeCount + 1
			};
		}

		case REMOVE_LINE_ITEM: {
			const { item, modifiedQuantity, modifiedTotalQuantity } = action.payload;
			let lines = { ...state.lines };
			const relatedBundleSideItems = collectBundleFreeGiftItems(state.lines, item);
			const itemsToRemove = [item, ...relatedBundleSideItems].filter(Boolean);
			const removeIds = new Set(itemsToRemove.map((entry) => entry?.id).filter(Boolean));

			let originalItem = state.lines[item.variantShopifyId];

			/* If the item is duplicated and the quantity is 0, and total quantity is greater than 0
			which means there are more duplicate then we need to remove the first duplicate and
			update the original item with the first duplicate*/
			if (item.isDuplicated && modifiedQuantity === 0 &&
				(modifiedTotalQuantity > 0 || item.duplicates.some(item => item.discountedPrice === 0))
			) {
				const [firstDuplicate, ...remainingDuplicates] = originalItem?.duplicates || [];
				originalItem = {
					...originalItem,
					...firstDuplicate,
					totalQuantity: modifiedTotalQuantity,
					duplicates: remainingDuplicates,
					isDuplicated: remainingDuplicates.length > 0,
				};
				lines[item.variantShopifyId] = originalItem;
			} else
				delete lines[item.variantShopifyId];

			lines = filterRemovedItemsFromBundleContainers(lines, removeIds);

			const linesToRemove = {
				...state.draftChanges.linesToRemove,
				...Object.fromEntries(itemsToRemove
					.filter((entry) => entry?.id)
					.map((entry) => [entry.id, entry]))
			};

			let linesToUpdate = { ...state.draftChanges.linesToUpdate };
			let linesToCreate = { ...state.draftChanges.linesToCreate };
			delete linesToCreate[item.id];
			delete linesToUpdate[item.id];
			linesToCreate = filterRemovedItemsFromBundleContainers(linesToCreate, removeIds);
			linesToUpdate = filterRemovedItemsFromBundleContainers(linesToUpdate, removeIds);

			return {
				...state,
				lines,
				draftChanges: {
					...state.draftChanges,
					linesToCreate,
					linesToUpdate,
					linesToRemove,
					unsaved: hasPendingChanges(linesToCreate, linesToUpdate, linesToRemove)
				},
				totalWeight: calculateTotalWeight(lines),
				totalLinesCount: calculateTotalLinesCount(lines),
				removeCount: state.removeCount + 1
			};
		}

		case CLEAR_SUBSCRIPTION_CART: {
			return {
				...state,
				lines: {},
				draftChanges: {
					...state.draftChanges,
					unsaved: true,
					linesToCreate: {},
					linesToUpdate: {},
					linesToRemove: {
						...state.draftChanges.linesToRemove,
						...Object.values(state.lines).reduce((acc, item) => {
							if (item.id)
								acc[item.id] = item;
							return acc;
						}, {})
					}
				},
				totalWeight: 0,
				totalLinesCount: 0,
				removeCount: 0,
			};
		}

		case CREATE_LINE_ITEM: {
			const { variantShopifyId } = action.payload;

			const itemsToRemove = Object.values(state.draftChanges.linesToRemove);
			const itemInRemoveList = itemsToRemove
				.find(item => {
					if (item.variantShopifyId === variantShopifyId) return item;
				});

			let lines = { ...state.lines },
				linesToCreate = { ...state.draftChanges.linesToCreate },
				linesToRemove = { ...state.draftChanges.linesToRemove },
				linesToUpdate = { ...state.draftChanges.linesToUpdate };

			// If the item is in the remove list, remove it from the remove list and add it back to the line items
			if (itemInRemoveList) {
				delete linesToRemove[itemInRemoveList.id];

				const item = {
					...itemInRemoveList,
					quantity: 1,
					totalQuantity: 1,
					isDuplicated: false,
					duplicates: []
				};

				lines = { ...lines, [variantShopifyId]: item };
				linesToUpdate = { ...linesToUpdate, [itemInRemoveList.id]: item };
			}
			else {
				lines = { ...lines, [variantShopifyId]: { ...action.payload, attributes: action.payload.customAttributes } };

				linesToCreate = { ...linesToCreate, [variantShopifyId]: action.payload };
			}

			return {
				...state,
				draftChanges: {
					...state.draftChanges,
					linesToCreate,
					linesToRemove,
					linesToUpdate,
					unsaved: hasPendingChanges(linesToCreate, linesToUpdate, linesToRemove)
				},
				lines,
				totalWeight: calculateTotalWeight(lines),
				totalLinesCount: calculateTotalLinesCount(lines),
				removeCount: 0
			};
		}

		case UPDATE_LINE_TO_CREATE: {
			const linesToCreate = { ...state.draftChanges.linesToCreate };
			const variantShopifyId = action.payload.item.variantShopifyId;
			const lines = { ...state.lines };

			if (action.payload.item.quantity <= 0) {
				delete linesToCreate[variantShopifyId];
				delete lines[variantShopifyId];
			}
			else {
				linesToCreate[variantShopifyId] = lines[variantShopifyId] = action.payload.item;
			}

			return {
				...state,
				draftChanges: {
					...state.draftChanges,
					linesToCreate,
					unsaved: hasPendingChanges(linesToCreate, state.draftChanges.linesToUpdate, state.draftChanges.linesToRemove)
				},
				lines,
				totalWeight: calculateTotalWeight(lines),
				totalLinesCount: calculateTotalLinesCount(lines),
				removeCount: action.payload.resetRemoveCount ? 0 : state.removeCount + 1
			};
		}

		case DISCARD_CHANGES: {
			return {
				...state,
				lines: state.originalLines,
				draftChanges: initialState.draftChanges,
				totalWeight: calculateTotalWeight(state.originalLines),
				totalLinesCount: calculateTotalLinesCount(state.originalLines),
				removeCount: 0
			};
		}
		case SET_HIGHLIGHT_BANNER:
			return {
				...state,
				highlightBanner: action.payload,
			};

		case SET_CLIENT_SUB_STATUS: {
			const status = action.payload;
			return {
				...state,
				status: status,
				clientSubStatus: status,
				isActive: status === "ACTIVE",
				isCancelled: status === "CANCELLED",
				isPaused: status === "PAUSED",
				isExpired: status === "EXPIRED",
			};
		}

		case getCustomerProfile.fulfilled.type: {
			const metafields = action.payload?.metafields || [];
			const statusMetafield = metafields.find(mf => mf?.key === "client_sub_status");
			const clientStatus = statusMetafield?.value || "ACTIVE";
			const sequentialDiscount = getCurrentDiscountStep(metafields);

			return {
				...state,
				status: clientStatus,
				clientSubStatus: clientStatus,
				isActive: clientStatus === "ACTIVE",
				isCancelled: clientStatus === "CANCELLED",
				isPaused: clientStatus === "PAUSED",
				isExpired: clientStatus === "EXPIRED",
				sequentialDiscount,
			};
		}

		case SET_AVAILABLE_SHIPPING_RATES:
			return {
				...state,
				availableShippingRates: action.payload.rates || [],
				shippingRatesLoading: action.payload.loading ?? false,
			};

		default:
			return state;
	}
};

export default subscriptionReducer;
